import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useInfiniteQuery } from '@tanstack/react-query';
import useInvoice from 'Hooks/useInvoice';
import {
    INVOICE_SUBTYPES,
    INVOICE_TYPES,
    INVOICES_RECONCILED_STATUS,
    PERIODS,
    CUSTOM_PERIOD,
    All_TIME_PERIOD,
    DRAWER_STACK,
    RESULTS_PER_PAGE,
} from 'Utils/constants';
import { getInvoiceReconciliationSuggestions } from 'Services/invoices';
import { trackError } from 'Utils/errorMonitoring';
import Loading from 'Components/Atoms/Loading/Loading';
import { useTranslation } from 'react-i18next';
import IconButton from 'Components/Atoms/IconButton/IconButton';
import OutlineArrowBottom from 'Components/Atoms/OutlineIcons/OutlineArrowBottom';
import OutlineCalendar from 'Components/Atoms/OutlineIcons/OutlineCalendar';
import OutlineAdd from 'Components/Atoms/OutlineIcons/OutlineAdd';
import OutlineFilter from 'Components/Atoms/OutlineIcons/OutlineFilter';
import OutlineReconciliation from 'Components/Atoms/OutlineIcons/OutlineReconciliation';
import Search from 'Components/Atoms/Search/Search';
import CardTransactionAsync from 'Components/Organisms/CardTransactionAsync/CardTransactionAsync';
import { cn } from 'Utils/cn';
import { getInvoiceReconciliationAmount, getReconciliationPendingAmount } from 'Utils/reconciliation';
import Icon from 'Components/Atoms/Icon/Icon';
import useInfiniteTransactions from 'Hooks/useInfiniteTransactions';
import useInfiniteContacts from 'Hooks/useInfiniteContacts';
import useProvidersConnections from 'Hooks/useProvidersConnections';
import useLocale from 'Hooks/useLocale';
import useDrawerStack from 'Hooks/useDrawerStack';
import ReconciliationSelectedTransactions from 'Pages/Reconciliation/ReconciliationSelectedTransactions';
import ReconciliationAmountMismatchDialog from 'Pages/Reconciliation/ReconciliationAmountMismatchDialog';
import useReconciliationMismatchDialog from 'Hooks/useReconciliationMismatchDialog';
import useReconciliationMutation from 'Hooks/useReconciliationMutation';
import ReconciliationTransactionItem from 'Components/Molecules/ReconciliationTransactionItem/ReconciliationTransactionItem';
import Select from 'Components/Atoms/Select/Select';
import Multiselect from 'Components/Molecules/Multiselect/Multiselect';
import MultiselectInfinite from 'Components/Molecules/MultiselectInfinite/MultiselectInfinite';
import CollapseContent from 'Components/Atoms/Collapse/CollapseContent';
import Dialog from 'Components/Molecules/Dialog/Dialog';
import DateRangeCalendar from 'Components/Molecules/DateRangeCalendar/DateRangeCalendar';
import Input from 'Components/Atoms/Input/Input';
import Button from 'Components/Atoms/Button/Button';
import ProgressBar from 'Components/Atoms/ProgressBar/ProgressBar';
import Tooltip from 'Components/Molecules/Tooltip/Tooltip';
import { formatCurrency, formatDateISO } from 'Utils/formatters';

const TRANSACTION_TYPE_ALL = 'all';
const TRANSACTION_TYPE_EXPENSE = 'expense';
const TRANSACTION_TYPE_INCOME = 'income';

const DEFAULT_FILTERS = {
    period: All_TIME_PERIOD,
    dateFrom: null,
    dateTo: null,
    productsIds: null,
    contactsIds: null,
    amountFrom: '',
    amountTo: '',
};

function useReconciliationData({ invoiceId, hasActiveFilters, transactionFilters, enabled }) {
    const suggestionsResult = useInfiniteQuery({
        queryKey: ['invoice-reconciliation-suggestions', invoiceId],
        queryFn: async ({ pageParam = 1 }) => {
            return getInvoiceReconciliationSuggestions(invoiceId, {
                query: null,
                page: pageParam,
                is_default: true,
            });
        },
        onError: (error) => trackError(error),
        getNextPageParam: (lastPage, allPages) => {
            const pagesLoaded = allPages.length;
            const totalPages = Math.ceil(lastPage.count / RESULTS_PER_PAGE);
            return pagesLoaded < totalPages ? pagesLoaded + 1 : false;
        },
        enabled: enabled && !hasActiveFilters,
    });

    const suggestionsLoaded = !suggestionsResult.isLoading && !!suggestionsResult.data;
    const suggestionsCount = suggestionsResult.data?.pages?.reduce((acc, p) => acc + p.items.length, 0) ?? 0;
    const noSuggestions = suggestionsLoaded && suggestionsCount === 0;

    const transactionsResult = useInfiniteTransactions(transactionFilters, {
        enabled: enabled && (hasActiveFilters || noSuggestions),
    });

    if (hasActiveFilters || noSuggestions) return transactionsResult;
    return suggestionsResult;
}

export default function InvoiceQuickEditorDrawerConciliation(props) {
    const { id } = props;
    const [query, setQuery] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const sentinelRef = useRef(null);
    const initialSyncDone = useRef(false);

    const { t } = useTranslation('invoices');
    const { t: tR } = useTranslation('reconciliation');

    const { invoice, reconcile, isReconciling } = useInvoice(id);
    const { saveInvoiceAssociations } = useReconciliationMutation();
    const isReconciled = invoice?.reconciled_status === INVOICES_RECONCILED_STATUS.RECONCILED;

    const [transactionsIds, setTransactionsIds] = useState([]);
    const [transactionsTotal, setTransactionsTotal] = useState(0);
    const [showExtraFilters, setShowExtraFilters] = useState(false);
    const [filters, setFilters] = useState(DEFAULT_FILTERS);

    const defaultTransactionType =
        invoice?.type === INVOICE_TYPES.PURCHASE ? TRANSACTION_TYPE_EXPENSE : TRANSACTION_TYPE_INCOME;
    const [transactionType, setTransactionType] = useState(null);
    const effectiveTransactionType = transactionType ?? defaultTransactionType;
    const [isCustomPeriodDialogOpen, setIsCustomPeriodDialogOpen] = useState(false);
    const [contactQuery, setContactQuery] = useState('');

    useEffect(() => {
        if (invoice && !initialSyncDone.current) {
            setTransactionsIds(invoice.transactions_associated ?? []);
            initialSyncDone.current = true;
        }
    }, [invoice]);

    const locale = useLocale();
    const { visibleProvidersConnections } = useProvidersConnections();
    const {
        data: contactsData,
        isFetching: isFetchingContacts,
        fetchNextPage: fetchNextPageContacts,
        hasNextPage: hasNextPageContacts,
    } = useInfiniteContacts({ query: contactQuery });

    const {
        showMismatchDialog,
        closeMismatchDialog,
        handleMismatchReconcile,
        handleMismatchSave,
        checkAndReconcile,
        forceReconcile,
        itemType,
        mismatchAmount,
    } = useReconciliationMismatchDialog({ type: 'invoice' });

    const { open } = useDrawerStack();

    const handleToggleTransaction = useCallback(
        (transactionId) => {
            const next = transactionsIds.includes(transactionId)
                ? transactionsIds.filter((tid) => tid !== transactionId)
                : [...transactionsIds, transactionId];
            setTransactionsIds(next);
            saveInvoiceAssociations(id, next);
        },
        [transactionsIds, saveInvoiceAssociations, id],
    );

    const handleDeleteTransaction = useCallback(
        (transactionId) => {
            const next = transactionsIds.filter((tid) => tid !== transactionId);
            setTransactionsIds(next);
            saveInvoiceAssociations(id, next);
        },
        [transactionsIds, saveInvoiceAssociations, id],
    );

    const handleOpenCreateTransaction = () => {
        open(DRAWER_STACK.CREATE_TRANSACTION, {
            defaultConcept: invoice?.identifier || '',
            defaultAmount: Math.round(pendingAmount * 100) / 100,
            title: tR('add-manual-transaction'),
            onSubmit: (createdTransaction) => {
                const next = [...transactionsIds, createdTransaction.id];
                setTransactionsIds(next);
                saveInvoiceAssociations(id, next);
            },
        });
    };

    const setFilter = useCallback((key, value) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
    }, []);

    const transactionTypeOptions = useMemo(
        () => [
            { label: tR('transaction-type.all'), value: TRANSACTION_TYPE_ALL },
            { label: tR('transaction-type.expense'), value: TRANSACTION_TYPE_EXPENSE },
            { label: tR('transaction-type.income'), value: TRANSACTION_TYPE_INCOME },
        ],
        [tR],
    );

    const transactionFilters = useMemo(() => {
        const base = {
            q: searchQuery || null,
            products_ids: filters.productsIds,
            contacts_ids: filters.contactsIds,
            amount_from: filters.amountFrom || null,
            amount_to: filters.amountTo || null,
            type: effectiveTransactionType === TRANSACTION_TYPE_ALL ? null : effectiveTransactionType,
        };
        if (filters.period === CUSTOM_PERIOD) {
            return {
                ...base,
                date_from: filters.dateFrom ? formatDateISO(filters.dateFrom) : null,
                date_to: filters.dateTo ? formatDateISO(filters.dateTo) : null,
            };
        }
        return { ...base, period: filters.period };
    }, [searchQuery, filters, effectiveTransactionType]);

    const hasActiveFilters =
        !!searchQuery ||
        filters.period !== DEFAULT_FILTERS.period ||
        filters.productsIds !== null ||
        filters.contactsIds !== null ||
        filters.amountFrom !== '' ||
        filters.amountTo !== '' ||
        transactionType !== null;

    const { data, isFetching, fetchNextPage, hasNextPage, isLoading } = useReconciliationData({
        invoiceId: id,
        hasActiveFilters,
        transactionFilters,
        enabled: !isReconciled,
    });

    const transactions = data?.pages?.reduce((acc, page) => [...acc, ...page.items], []) ?? [];

    useEffect(() => {
        if (isFetching) return;

        if (sentinelRef.current) {
            const observer = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        fetchNextPage();
                    }
                });
            });

            observer.observe(sentinelRef.current);

            return () => {
                observer.disconnect();
            };
        }
    }, [sentinelRef, isFetching]);

    const productOptions = useMemo(() => {
        return visibleProvidersConnections.flatMap((connection) =>
            connection.products.map((product) => ({
                id: product.id,
                label: product.name,
                icon: <img src={connection.logo_url} height="18" width="18" alt="" />,
            })),
        );
    }, [visibleProvidersConnections]);

    const contactOptions = useMemo(() => {
        return (
            contactsData?.pages?.reduce((acc, page) => {
                return [
                    ...acc,
                    ...page.items.map((contact) => ({
                        id: contact.id,
                        label: contact.name,
                    })),
                ];
            }, []) ?? []
        );
    }, [contactsData]);

    const periodOptions = useMemo(() => {
        return PERIODS.flatMap((period) => {
            if (period.value === CUSTOM_PERIOD) {
                return [{ type: 'divider' }, { label: tR(period.i18n), value: period.value }];
            }
            return { label: tR(period.i18n), value: period.value };
        });
    }, [tR]);

    const periodLabel = useMemo(() => {
        if (filters.period === CUSTOM_PERIOD && filters.dateFrom && filters.dateTo) {
            const from = filters.dateFrom.toLocaleDateString(locale, { day: '2-digit', month: '2-digit' });
            const to = filters.dateTo.toLocaleDateString(locale, { day: '2-digit', month: '2-digit' });
            return `${from} - ${to}`;
        }
        const selectedPeriod = PERIODS.find((p) => p.value === filters.period);
        return selectedPeriod ? tR(selectedPeriod.i18n) : tR('periods.all-time');
    }, [filters.period, filters.dateFrom, filters.dateTo, tR, locale]);

    const productsLabel = useMemo(() => {
        if (!filters.productsIds || filters.productsIds.length === 0) return tR('products');
        const selected = productOptions.find((opt) => filters.productsIds.includes(opt.id));
        if (filters.productsIds.length === 1 && selected) return selected.label;
        return `${tR('products')} (${filters.productsIds.length})`;
    }, [filters.productsIds, productOptions, tR]);

    const contactsLabel = useMemo(() => {
        if (!filters.contactsIds || filters.contactsIds.length === 0) return tR('contacts');
        const selected = contactOptions.find((opt) => filters.contactsIds.includes(opt.id));
        if (filters.contactsIds.length === 1 && selected) return selected.label;
        return `${tR('contacts')} (${filters.contactsIds.length})`;
    }, [filters.contactsIds, contactOptions, tR]);

    const handleChangePeriod = (value) => {
        if (value === CUSTOM_PERIOD) {
            setIsCustomPeriodDialogOpen(true);
            return;
        }
        setFilter('period', value);
        setFilter('dateFrom', null);
        setFilter('dateTo', null);
    };

    const handleChangeCustomPeriod = (range) => {
        setIsCustomPeriodDialogOpen(false);
        if (!range) return;
        setFilter('period', CUSTOM_PERIOD);
        setFilter('dateFrom', range[0]);
        setFilter('dateTo', range[1]);
    };

    const isReverse = [INVOICE_SUBTYPES.STANDARD_CORRECTIVE, INVOICE_SUBTYPES.SIMPLIFIED_CORRECTIVE].includes(
        invoice?.subtype,
    );
    const amountWithType = invoice?.type === 'purchase' ? (invoice?.amount ?? 0) * -1 : (invoice?.amount ?? 0);
    const displayAmount = isReverse ? amountWithType * -1 : amountWithType;
    const normalizedInvoiceAmount = getInvoiceReconciliationAmount(invoice);
    const pendingAmount = getReconciliationPendingAmount(normalizedInvoiceAmount, transactionsTotal);

    const isQuoteProformaOrDeliveryNote = [
        INVOICE_SUBTYPES.QUOTE,
        INVOICE_SUBTYPES.PROFORMA,
        INVOICE_SUBTYPES.DELIVERY_NOTE,
    ].includes(invoice?.subtype);

    if (isQuoteProformaOrDeliveryNote) {
        const typeLabelMap = {
            [INVOICE_SUBTYPES.QUOTE]: t('invoice-quote'),
            [INVOICE_SUBTYPES.PROFORMA]: t('invoice-proforma'),
            [INVOICE_SUBTYPES.DELIVERY_NOTE]: t('invoice-delivery-note'),
        };

        return (
            <div className="py-6 text-center flex flex-col items-center gap-2">
                <Icon name="uncategorized" color="currentColor" className="text-gray-300" size={48} />
                <div className="text-dark-blue font-semibold text-pretty text-sm">
                    {t('non-linkable-document-title', { typeLabel: typeLabelMap[invoice?.subtype] })}
                </div>
                <div className="text-dark-blue/50 text-sm">{t('non-linkable-document-description')}</div>
            </div>
        );
    }

    return (
        <>
            <ReconciliationAmountMismatchDialog
                open={showMismatchDialog}
                onClose={closeMismatchDialog}
                onReconcile={handleMismatchReconcile}
                onSave={handleMismatchSave}
                itemType={itemType}
                mismatchAmount={mismatchAmount}
                currency={invoice?.currency ?? 'EUR'}
            />

            {isReconciled && (
                <>
                    {invoice?.transactions_associated.length === 0 && (
                        <div className="text-center text-gray-400 py-6 text-sm border border-gray-200 rounded-lg mt-3">
                            {tR('reconciled-without-transactions')}
                        </div>
                    )}
                    {invoice?.transactions_associated.length > 0 && (
                        <>
                            <div className="flex items-center py-2">
                                <div className="text-2xs uppercase tracking-wider font-semibold text-gray-500">
                                    {t('related-transactions')}
                                </div>
                            </div>
                            <div className="grid gap-2">
                                {invoice.transactions_associated.map((transactionId) => (
                                    <CardTransactionAsync key={transactionId} transactionId={transactionId} />
                                ))}
                            </div>
                        </>
                    )}
                    <div className="flex justify-center mt-4">
                        <Button
                            size="small"
                            variant="ghost"
                            disabled={isReconciling}
                            onClick={() =>
                                reconcile({
                                    transactionsIds: invoice?.transactions_associated,
                                    forceReconciliationStatus: false,
                                    forceTransactionsReconciliationStatus: false,
                                })
                            }
                        >
                            {tR('unreconcile')}
                        </Button>
                    </div>
                </>
            )}

            {!isReconciled && (
                <>
                    <Tooltip
                        label={
                            <div className="grid gap-1.5 text-xs min-w-[180px] py-1">
                                <div className="flex justify-between gap-4">
                                    <span>{tR('total')}</span>
                                    <span className="font-mono">
                                        {formatCurrency(displayAmount, {
                                            currency: invoice?.currency,
                                            locale,
                                        })}
                                    </span>
                                </div>
                                <div className="flex justify-between gap-4">
                                    <span>{tR('reconciled-label')}</span>
                                    <span className="font-mono">
                                        {formatCurrency(transactionsTotal, {
                                            currency: invoice?.currency,
                                            locale,
                                        })}
                                    </span>
                                </div>
                                <div
                                    className={cn('flex justify-between gap-4', pendingAmount !== 0 && 'text-warning')}
                                >
                                    <span>{pendingAmount < 0 ? tR('difference-label') : tR('pending-label')}</span>
                                    <span className="font-mono">
                                        {formatCurrency(pendingAmount, {
                                            currency: invoice?.currency,
                                            locale,
                                        })}
                                    </span>
                                </div>
                            </div>
                        }
                    >
                        <div className="py-1 cursor-help">
                            <ProgressBar
                                value={Math.abs(transactionsTotal)}
                                max={Math.abs(normalizedInvoiceAmount) || 1}
                                color={pendingAmount === 0 ? 'success' : pendingAmount < 0 ? 'danger' : 'warning'}
                                bottomLeftText={formatCurrency(transactionsTotal, {
                                    currency: invoice?.currency,
                                    locale,
                                })}
                                bottomRightText={`${pendingAmount < 0 ? tR('difference-label') : tR('pending-label')}: ${formatCurrency(pendingAmount, { currency: invoice?.currency, locale })}`}
                            />
                        </div>
                    </Tooltip>

                    <ReconciliationSelectedTransactions
                        transactionsIds={transactionsIds}
                        onDeleteTransaction={handleDeleteTransaction}
                        onChangeTotal={setTransactionsTotal}
                    />
                    {invoice?.tax_authority_provider_status !== 'cancelled' && (
                        <>
                            <div className="flex flex-wrap mt-3 gap-2 mb-1">
                                {transactionsIds.length === 0 && (
                                    <Button size="small" className="grow" onClick={() => forceReconcile(invoice.id)}>
                                        <OutlineReconciliation size={16} color="currentColor" />
                                        {tR('reconcile-without-transaction')}
                                    </Button>
                                )}
                                {transactionsIds.length > 0 && (
                                    <Button
                                        size="small"
                                        className="grow"
                                        variant={pendingAmount === 0 ? 'primary' : 'warning'}
                                        onClick={() => checkAndReconcile(invoice.id, transactionsIds, pendingAmount)}
                                    >
                                        <Icon name="circle-check" color="currentColor" size={16} />
                                        {tR('reconcile')}
                                    </Button>
                                )}
                                <Button size="small" className="grow" onClick={handleOpenCreateTransaction}>
                                    <OutlineAdd />
                                    {tR('add-manual-transaction')}
                                </Button>
                            </div>

                            <div>
                                <div className="min-h-[240px] mt-3">
                                    <div className="flex items-center gap-1 mb-2">
                                        <Search
                                            className="grow"
                                            placeholder={t('search-transaction-placeholder')}
                                            value={query}
                                            onChange={setQuery}
                                            onSearch={() => setSearchQuery(query)}
                                            onClear={() => {
                                                setQuery('');
                                                setSearchQuery('');
                                            }}
                                        />
                                        <IconButton
                                            icon={
                                                showExtraFilters ? <Icon name="close" size={24} /> : <OutlineFilter />
                                            }
                                            onClick={() => setShowExtraFilters((prev) => !prev)}
                                            pressed={showExtraFilters}
                                        />
                                    </div>

                                    <CollapseContent open={showExtraFilters}>
                                        <div className="pt-2 pb-3 grid gap-2">
                                            <div className="grid grid-cols-2 gap-2">
                                                <Select
                                                    size="small"
                                                    className="w-full"
                                                    options={periodOptions}
                                                    icon={<OutlineCalendar className="text-dark-blue" />}
                                                    onChange={handleChangePeriod}
                                                    value={filters.period}
                                                    placeholder={periodLabel}
                                                    enableClear={filters.period !== DEFAULT_FILTERS.period}
                                                    onClear={() => {
                                                        setFilter('period', DEFAULT_FILTERS.period);
                                                        setFilter('dateFrom', null);
                                                        setFilter('dateTo', null);
                                                    }}
                                                />
                                                <Select
                                                    size="small"
                                                    className="w-full"
                                                    options={transactionTypeOptions}
                                                    onChange={(v) => setTransactionType(v)}
                                                    value={effectiveTransactionType}
                                                    enableClear={transactionType !== null}
                                                    onClear={() => setTransactionType(null)}
                                                />
                                            </div>
                                            <div className="grid grid-cols-2 gap-2">
                                                <Multiselect
                                                    size="small"
                                                    className="w-full"
                                                    label={productsLabel}
                                                    options={productOptions}
                                                    optionsSelected={filters.productsIds ?? []}
                                                    onChangeSelected={(ids) =>
                                                        setFilter('productsIds', ids?.length > 0 ? ids : null)
                                                    }
                                                    enableClear={filters.productsIds && filters.productsIds.length > 0}
                                                    onClear={() => setFilter('productsIds', null)}
                                                />
                                                <MultiselectInfinite
                                                    size="small"
                                                    label={contactsLabel}
                                                    options={contactOptions}
                                                    optionsSelected={filters.contactsIds ?? []}
                                                    onChangeSelected={(ids) =>
                                                        setFilter('contactsIds', ids?.length > 0 ? ids : null)
                                                    }
                                                    query={contactQuery}
                                                    onChangeQuery={setContactQuery}
                                                    isFetching={isFetchingContacts}
                                                    fetchNextPage={fetchNextPageContacts}
                                                    hasNextPage={hasNextPageContacts}
                                                    enableClear={filters.contactsIds && filters.contactsIds.length > 0}
                                                    onClear={() => setFilter('contactsIds', null)}
                                                />
                                            </div>
                                            <div className="grid grid-cols-2 gap-2">
                                                <Input
                                                    type="number"
                                                    placeholder={tR('from-placeholder')}
                                                    value={filters.amountFrom}
                                                    onChange={(ev) => setFilter('amountFrom', ev.target.value)}
                                                    size="small"
                                                />
                                                <Input
                                                    type="number"
                                                    placeholder={tR('to-placeholder')}
                                                    value={filters.amountTo}
                                                    onChange={(ev) => setFilter('amountTo', ev.target.value)}
                                                    size="small"
                                                />
                                            </div>
                                        </div>
                                    </CollapseContent>

                                    <Dialog
                                        open={isCustomPeriodDialogOpen}
                                        onClose={() => setIsCustomPeriodDialogOpen(false)}
                                    >
                                        <DateRangeCalendar onChange={handleChangeCustomPeriod} />
                                    </Dialog>

                                    {isFetching && isLoading && (
                                        <div className="text-center py-6">
                                            <Loading />
                                        </div>
                                    )}

                                    {!isLoading && (
                                        <>
                                            {transactions.length === 0 && (
                                                <div className="text-center text-gray-500 py-6 text-sm">
                                                    {tR('no-transactions-found')}
                                                </div>
                                            )}

                                            {transactions.length > 0 && (
                                                <div className="grid border border-dark-blue/10 rounded-lg overflow-hidden mt-2">
                                                    {transactions.map((transaction) => (
                                                        <ReconciliationTransactionItem
                                                            key={transaction.id}
                                                            invoice={invoice}
                                                            transaction={transaction}
                                                            checked={transactionsIds.includes(transaction.id)}
                                                            onClick={() => handleToggleTransaction(transaction.id)}
                                                        />
                                                    ))}
                                                </div>
                                            )}

                                            <div className="mt-6 text-center text-gray-500 text-xs">
                                                {hasNextPage &&
                                                    (isFetching ? (
                                                        <Loading size="small" />
                                                    ) : (
                                                        <IconButton
                                                            variant="ghost"
                                                            icon={<OutlineArrowBottom />}
                                                            onClick={fetchNextPage}
                                                        />
                                                    ))}
                                            </div>

                                            {hasNextPage && <div ref={sentinelRef} />}
                                        </>
                                    )}
                                </div>
                            </div>
                        </>
                    )}

                    {invoice?.tax_authority_provider_status === 'cancelled' && (
                        <div className="text-center text-gray-400 py-6 text-sm">
                            {t('cancelled-invoice-no-transactions')}
                        </div>
                    )}
                </>
            )}
        </>
    );
}

InvoiceQuickEditorDrawerConciliation.propTypes = {
    id: PropTypes.string.isRequired,
};
